import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { Menu, X, ChevronRight, ChevronDown, Box, Warehouse, Factory, Database, Ship, Truck, Cog, PackageSearch, Users, HelpCircle, FileText, Newspaper, Zap, Handshake, TrendingUp, ShieldCheck, Compass, Wrench, Rocket, Layers, LineChart, Sparkles, Briefcase } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';

const PLATFORM_MENU = [
  {
    id: 'viso-yard',
    name: 'Viso Yard',
    icon: Box,
    tag: 'Ports · Terminals · Yards',
    items: [
      { label: 'Container Vision', hash: 'container-vision' },
      { label: 'Tank Vision', hash: 'tank-vision' },
      { label: 'Gate Vision', hash: 'gate-vision' },
      { label: 'Yard Vision', hash: 'yard-vision' },
      { label: 'Crane Vision', hash: 'crane-vision' },
      { label: 'Cargo Vision', hash: 'cargo-vision' },
      { label: 'Document Vision', hash: 'document-vision' },
      { label: 'Work Vision', hash: 'work-vision' },
      { label: 'Secure Vision', hash: 'secure-vision' },
    ],
  },
  {
    id: 'viso-warehouse',
    name: 'Viso Warehouse',
    icon: Warehouse,
    tag: 'DCs · 3PL · Cold chain',
    items: [
      { label: 'Cargo Vision', hash: 'cargo-vision' },
      { label: 'Audit Vision', hash: 'audit-vision' },
      { label: 'Dimension Vision', hash: 'dimension-vision' },
      { label: 'Document Vision', hash: 'document-vision' },
      { label: 'Work Vision', hash: 'work-vision' },
      { label: 'Secure Vision', hash: 'secure-vision' },
    ],
  },
  {
    id: 'viso-factory',
    name: 'Viso Factory',
    icon: Factory,
    tag: 'Production · Quality · Safety',
    items: [
      { label: 'Production Vision', hash: 'production-vision' },
      { label: 'Audit Vision', hash: 'audit-vision' },
      { label: 'Dimension Vision', hash: 'dimension-vision' },
      { label: 'Work Vision', hash: 'work-vision' },
      { label: 'Secure Vision', hash: 'secure-vision' },
    ],
  },
  {
    id: 'viso-data',
    name: 'Viso Data',
    icon: Database,
    tag: 'Foundation AI',
    items: [
      { label: 'Compression AI', hash: 'compression-ai' },
      { label: 'Trace AI', hash: 'trace-ai' },
      { label: 'Detect AI', hash: 'detect-ai' },
    ],
  },
];

const INDUSTRIES_MENU = [
  {
    id: 'ports-terminals',
    name: 'Ports & Terminals',
    icon: Ship,
    tag: 'Seaports · Container yards · ICDs',
    desc: 'Gate, crane and yard vision for terminals.',
  },
  {
    id: 'warehousing-distribution',
    name: 'Warehousing & Distribution',
    icon: Warehouse,
    tag: 'DCs · 3PL · Cold chain',
    desc: 'Count, dimension and prove every order.',
  },
  {
    id: 'manufacturing',
    name: 'Manufacturing',
    icon: Cog,
    tag: 'Line · Quality · Safety',
    desc: 'Count, SKU and inspect on a single camera.',
  },
  {
    id: 'logistics-supply-chain',
    name: 'Logistics & Supply Chain',
    icon: Truck,
    tag: 'Depots · Docks · Cross-dock',
    desc: 'Chain of custody across the network.',
  },
];

const COMPANY_MENU = [
  {
    id: 'about',
    name: 'About',
    icon: Users,
    tag: 'Our story, team & mission',
    desc: 'Builders of AI for the physical world.',
    to: '/company/about',
  },
  {
    id: 'careers',
    name: 'Careers',
    icon: Briefcase,
    tag: 'Come build with us',
    desc: 'Build the vision layer of physical operations.',
    to: '/company/careers',
  },
];

const RESOURCES_MENU = [
  {
    id: 'blog',
    name: 'Blog',
    icon: Newspaper,
    tag: 'Engineering & product notes',
    desc: 'Field notes on AI vision for logistics.',
    to: '/resources/blog',
  },
  {
    id: 'faqs',
    name: 'FAQs',
    icon: HelpCircle,
    tag: 'Common questions',
    desc: 'Everything about the platform.',
    to: '/resources/faqs',
  },
];

const SERVICES_MENU = [
  { id: 'stage-01', name: 'Assess', icon: Compass, tag: 'Stage 01', desc: 'Site survey, camera & edge audit, ROI model.', to: '/services#stage-01' },
  { id: 'stage-02', name: 'Design', icon: Layers, tag: 'Stage 02', desc: 'Architecture blueprint, model catalog, policies.', to: '/services#stage-02' },
  { id: 'stage-03', name: 'Build', icon: Wrench, tag: 'Stage 03', desc: 'Pipeline build, model fine-tune, twin authoring.', to: '/services#stage-03' },
  { id: 'stage-04', name: 'Integrate', icon: Database, tag: 'Stage 04', desc: 'TOS · WMS · AODB · ERP · BI + data lake.', to: '/services#stage-04' },
  { id: 'stage-05', name: 'Deploy', icon: Rocket, tag: 'Stage 05', desc: 'Edge rollout, multi-site scale, SRE handover.', to: '/services#stage-05' },
  { id: 'stage-06', name: 'Operate', icon: Cog, tag: 'Stage 06', desc: '24x7 MDR for vision, model drift care, SLAs.', to: '/services#stage-06' },
  { id: 'stage-07', name: 'Optimize', icon: LineChart, tag: 'Stage 07', desc: 'Continuous tuning, new class onboarding.', to: '/services#stage-07' },
  { id: 'stage-08', name: 'Transform', icon: Sparkles, tag: 'Stage 08', desc: 'Autonomous ops, cross-site playbooks, scale-up.', to: '/services#stage-08' },
];

const PARTNERS_MENU = [
  { id: 'register-deals', name: 'Register deals', icon: Handshake, tag: 'Deal protection', desc: 'Protect pipeline with deal registration and approval workflows.', to: '/partners#register-deals' },
  { id: 'grow-with-us', name: 'Grow with us', icon: TrendingUp, tag: 'Enablement', desc: 'Enablement, pre-sales support and co-marketing.', to: '/partners#grow-with-us' },
  { id: 'certified-delivery', name: 'Certified delivery', icon: ShieldCheck, tag: 'Delivery', desc: 'Deliver Visotonics services with certified playbooks.', to: '/partners#certified-delivery' },
  { id: 'partner-register', name: 'Become a partner', icon: Users, tag: 'Registration', desc: 'Sign up as a Visotonics partner to unlock deal rights.', to: '/partner/register' },
];

function PlatformMegaMenu({ onClose }) {
  return (
    <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-[min(1120px,96vw)] z-40" onMouseLeave={onClose}>
      <div className="rounded-2xl border border-white/10 bg-[#0a0d12]/95 backdrop-blur-xl shadow-2xl shadow-black/60 overflow-hidden">
        <div className="px-8 pt-6 pb-4 border-b border-white/5">
          <div className="text-xs font-mono uppercase tracking-widest text-cyan-300">Platform</div>
          <div className="font-display text-lg text-slate-100 mt-1">Four environments, one vision platform.</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 gap-0">
          {PLATFORM_MENU.map((col, idx) => (
            <div
              key={col.id}
              className={`p-6 ${idx !== PLATFORM_MENU.length - 1 ? 'md:border-r border-white/5' : ''}`}
            >
              <div className="flex items-center gap-2 mb-1">
                <div className="w-8 h-8 rounded-md bg-cyan-400/10 border border-cyan-400/20 flex items-center justify-center">
                  <col.icon className="w-4 h-4 text-cyan-300" />
                </div>
                <Link
                  to={`/platform/${col.id}`}
                  onClick={onClose}
                  className="font-display font-semibold text-slate-100 hover:text-cyan-300 transition-colors"
                >
                  {col.name}
                </Link>
              </div>
              <div className="text-[11px] uppercase tracking-widest text-slate-500 mb-3 ml-10">{col.tag}</div>
              <ul className="space-y-1.5">
                {col.items.map((it) => (
                  <li key={it.hash}>
                    <Link
                      to={`/platform/${col.id}#${it.hash}`}
                      onClick={onClose}
                      className="group flex items-center justify-between text-sm text-slate-300 hover:text-cyan-300 py-1 transition-colors"
                    >
                      <span>{it.label}</span>
                      <ChevronRight className="w-3 h-3 opacity-0 group-hover:opacity-100 -translate-x-1 group-hover:translate-x-0 transition-all" />
                    </Link>
                  </li>
                ))}
              </ul>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function IndustriesMegaMenu({ onClose }) {
  return (
    <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 w-[min(960px,96vw)] z-40" onMouseLeave={onClose}>
      <div className="rounded-2xl border border-white/10 bg-[#0a0d12]/95 backdrop-blur-xl shadow-2xl shadow-black/60 overflow-hidden">
        <div className="px-8 pt-6 pb-4 border-b border-white/5">
          <div className="text-xs font-mono uppercase tracking-widest text-cyan-300">Industries</div>
          <div className="font-display text-lg text-slate-100 mt-1">AI vision tuned to four industries.</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-0">
          {INDUSTRIES_MENU.map((row, idx) => (
            <Link
              key={row.id}
              to={`/industries/${row.id}`}
              onClick={onClose}
              className={`group p-6 flex items-start gap-4 border-white/5 hover:bg-white/[0.03] transition-colors ${idx % 2 === 0 ? 'md:border-r' : ''} ${idx < INDUSTRIES_MENU.length - 2 ? 'md:border-b' : ''}`}
            >
              <div className="w-10 h-10 rounded-md bg-cyan-400/10 border border-cyan-400/20 flex items-center justify-center flex-shrink-0">
                <row.icon className="w-5 h-5 text-cyan-300" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-display font-semibold text-slate-100 group-hover:text-cyan-300 transition-colors">
                    {row.name}
                  </div>
                  <ChevronRight className="w-3.5 h-3.5 text-slate-500 group-hover:text-cyan-300 group-hover:translate-x-0.5 transition-all" />
                </div>
                <div className="text-[11px] uppercase tracking-widest text-slate-500 mt-1">{row.tag}</div>
                <div className="text-xs text-slate-400 mt-2">{row.desc}</div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

function SimpleMegaMenu({ title, subtitle, items, onClose, width = 'min(720px,96vw)' }) {
  return (
    <div className="absolute left-1/2 -translate-x-1/2 top-full pt-3 z-40" style={{ width }} onMouseLeave={onClose}>
      <div className="rounded-2xl border border-white/10 bg-[#0a0d12]/95 backdrop-blur-xl shadow-2xl shadow-black/60 overflow-hidden">
        <div className="px-8 pt-6 pb-4 border-b border-white/5">
          <div className="text-xs font-mono uppercase tracking-widest text-cyan-300">{title}</div>
          <div className="font-display text-lg text-slate-100 mt-1">{subtitle}</div>
        </div>
        <div className={`grid grid-cols-1 ${items.length > 1 ? 'md:grid-cols-2' : ''} gap-0`}>
          {items.map((row, idx) => (
            <Link
              key={row.id}
              to={row.to || '#'}
              onClick={onClose}
              className={`group p-6 flex items-start gap-4 border-white/5 hover:bg-white/[0.03] transition-colors ${items.length > 1 && idx % 2 === 0 ? 'md:border-r' : ''}`}
            >
              <div className="w-10 h-10 rounded-md bg-cyan-400/10 border border-cyan-400/20 flex items-center justify-center flex-shrink-0">
                <row.icon className="w-5 h-5 text-cyan-300" />
              </div>
              <div className="flex-1">
                <div className="flex items-center justify-between gap-2">
                  <div className="font-display font-semibold text-slate-100 group-hover:text-cyan-300 transition-colors">
                    {row.name}
                  </div>
                  <ChevronRight className="w-3.5 h-3.5 text-slate-500 group-hover:text-cyan-300 group-hover:translate-x-0.5 transition-all" />
                </div>
                <div className="text-[11px] uppercase tracking-widest text-slate-500 mt-1">{row.tag}</div>
                <div className="text-xs text-slate-400 mt-2">{row.desc}</div>
              </div>
            </Link>
          ))}
        </div>
      </div>
    </div>
  );
}

export default function Header() {
  const [open, setOpen] = React.useState(false);
  const [openMenu, setOpenMenu] = React.useState(null); // 'platform' | 'industries' | null
  const closeTimerRef = React.useRef(null);
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const scheduleClose = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    closeTimerRef.current = setTimeout(() => setOpenMenu(null), 120);
  };
  const cancelClose = () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
  };

  const nav = [
    { to: '/contact', label: 'Contact' },
  ];

  return (
    <header
      className="sticky top-0 z-50 backdrop-blur-md bg-[#07090c]/70 border-b border-white/5"
      onMouseLeave={scheduleClose}
    >
      <div className="max-w-7xl mx-auto px-6 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2 font-display font-bold text-lg">
          <img
            src="/logo.png"
            alt="Visotonics"
            className="h-8 w-auto"
          />
        </Link>

        <nav className="hidden md:flex items-center gap-1 relative">
          {/* Platform trigger */}
          <div onMouseEnter={() => { cancelClose(); setOpenMenu('platform'); }} className="relative">
            <Link
              to="/platform"
              className={`inline-flex items-center gap-1 px-3 py-2 text-sm rounded-md transition-colors ${location.pathname.startsWith('/platform') || openMenu === 'platform' ? 'text-cyan-300' : 'text-slate-300 hover:text-white'}`}
            >
              Platform <ChevronDown className={`w-3.5 h-3.5 transition-transform ${openMenu === 'platform' ? 'rotate-180' : ''}`} />
            </Link>
          </div>

          {/* Industries trigger */}
          <div onMouseEnter={() => { cancelClose(); setOpenMenu('industries'); }} className="relative">
            <Link
              to="/industries"
              className={`inline-flex items-center gap-1 px-3 py-2 text-sm rounded-md transition-colors ${location.pathname.startsWith('/industries') || openMenu === 'industries' ? 'text-cyan-300' : 'text-slate-300 hover:text-white'}`}
            >
              Industries <ChevronDown className={`w-3.5 h-3.5 transition-transform ${openMenu === 'industries' ? 'rotate-180' : ''}`} />
            </Link>
          </div>

          {/* Resources trigger */}
          <div onMouseEnter={() => { cancelClose(); setOpenMenu('resources'); }} className="relative">
            <Link
              to="/resources/blog"
              className={`inline-flex items-center gap-1 px-3 py-2 text-sm rounded-md transition-colors ${location.pathname.startsWith('/resources') || openMenu === 'resources' ? 'text-cyan-300' : 'text-slate-300 hover:text-white'}`}
            >
              Resources <ChevronDown className={`w-3.5 h-3.5 transition-transform ${openMenu === 'resources' ? 'rotate-180' : ''}`} />
            </Link>
          </div>

          {/* Services trigger */}
          <div onMouseEnter={() => { cancelClose(); setOpenMenu('services'); }} className="relative">
            <Link
              to="/services"
              className={`inline-flex items-center gap-1 px-3 py-2 text-sm rounded-md transition-colors ${location.pathname.startsWith('/services') || openMenu === 'services' ? 'text-cyan-300' : 'text-slate-300 hover:text-white'}`}
            >
              Services <ChevronDown className={`w-3.5 h-3.5 transition-transform ${openMenu === 'services' ? 'rotate-180' : ''}`} />
            </Link>
          </div>

          {/* Partners trigger */}
          <div onMouseEnter={() => { cancelClose(); setOpenMenu('partners'); }} className="relative">
            <Link
              to="/partners"
              className={`inline-flex items-center gap-1 px-3 py-2 text-sm rounded-md transition-colors ${location.pathname.startsWith('/partners') || openMenu === 'partners' ? 'text-cyan-300' : 'text-slate-300 hover:text-white'}`}
            >
              Partners <ChevronDown className={`w-3.5 h-3.5 transition-transform ${openMenu === 'partners' ? 'rotate-180' : ''}`} />
            </Link>
          </div>

          {/* Company trigger */}
          <div onMouseEnter={() => { cancelClose(); setOpenMenu('company'); }} className="relative">
            <Link
              to="/company/about"
              className={`inline-flex items-center gap-1 px-3 py-2 text-sm rounded-md transition-colors ${location.pathname.startsWith('/company') || openMenu === 'company' ? 'text-cyan-300' : 'text-slate-300 hover:text-white'}`}
            >
              Company <ChevronDown className={`w-3.5 h-3.5 transition-transform ${openMenu === 'company' ? 'rotate-180' : ''}`} />
            </Link>
          </div>

          {nav.map((n) => (
            <Link
              key={n.to}
              to={n.to}
              onMouseEnter={() => { cancelClose(); setOpenMenu(null); }}
              className={`px-3 py-2 text-sm rounded-md transition-colors ${location.pathname.startsWith(n.to) ? 'text-cyan-300' : 'text-slate-300 hover:text-white'}`}
            >
              {n.label}
            </Link>
          ))}
        </nav>

        <div className="hidden md:flex items-center gap-2" onMouseEnter={() => setOpenMenu(null)}>
          {user ? (
            <>
              <Link to={user.role === 'partner' ? '/partner/dashboard' : '/dashboard'} className="text-sm text-slate-300 hover:text-white px-3 py-2">
                {user.name.split(' ')[0]}
              </Link>
              <Link to="/account" className="text-sm text-slate-300 hover:text-white px-3 py-2">Account</Link>
              <Button variant="outline" onClick={() => { logout(); navigate('/'); }} className="border-white/10 bg-transparent text-slate-200 hover:bg-white/5">Log out</Button>
            </>
          ) : (
            <>
              <Link to="/login" className="text-sm text-slate-300 hover:text-white px-3 py-2">Log in</Link>
              <Link to="/contact">
                <Button className="bg-cyan-400 hover:bg-cyan-300 text-slate-900 font-medium">
                  Book a demo <ChevronRight className="w-4 h-4 ml-1" />
                </Button>
              </Link>
            </>
          )}
        </div>

        <button className="md:hidden p-2 text-slate-200" onClick={() => setOpen((v) => !v)} aria-label="menu">
          {open ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </button>
      </div>

      {/* Desktop mega menus */}
      {openMenu === 'platform' && (
        <div className="hidden md:block relative" onMouseEnter={cancelClose}>
          <PlatformMegaMenu onClose={() => setOpenMenu(null)} />
        </div>
      )}
      {openMenu === 'industries' && (
        <div className="hidden md:block relative" onMouseEnter={cancelClose}>
          <IndustriesMegaMenu onClose={() => setOpenMenu(null)} />
        </div>
      )}
      {openMenu === 'company' && (
        <div className="hidden md:block relative" onMouseEnter={cancelClose}>
          <SimpleMegaMenu
            title="Company"
            subtitle="Get to know Visotonics."
            items={COMPANY_MENU}
            width="min(520px,96vw)"
            onClose={() => setOpenMenu(null)}
          />
        </div>
      )}
      {openMenu === 'resources' && (
        <div className="hidden md:block relative" onMouseEnter={cancelClose}>
          <SimpleMegaMenu
            title="Resources"
            subtitle="Read, watch, learn."
            items={RESOURCES_MENU}
            width="min(720px,96vw)"
            onClose={() => setOpenMenu(null)}
          />
        </div>
      )}
      {openMenu === 'services' && (
        <div className="hidden md:block relative" onMouseEnter={cancelClose}>
          <SimpleMegaMenu
            title="Services"
            subtitle="Eight stages. One outcome — autonomous operations."
            items={SERVICES_MENU}
            width="min(1040px,96vw)"
            onClose={() => setOpenMenu(null)}
          />
        </div>
      )}
      {openMenu === 'partners' && (
        <div className="hidden md:block relative" onMouseEnter={cancelClose}>
          <SimpleMegaMenu
            title="Partners"
            subtitle="Build, sell and deliver Physical AI with Visotonics."
            items={PARTNERS_MENU}
            width="min(960px,96vw)"
            onClose={() => setOpenMenu(null)}
          />
        </div>
      )}

      {/* Mobile menu */}
      {open && (
        <div className="md:hidden border-t border-white/5 bg-[#07090c]/95">
          <div className="px-6 py-4 flex flex-col gap-1">
            <Link to="/platform" onClick={() => setOpen(false)} className="py-2 text-sm text-slate-200 font-semibold">Platform</Link>
            {PLATFORM_MENU.map((col) => (
              <div key={col.id} className="pl-3 py-1">
                <Link to={`/platform/${col.id}`} onClick={() => setOpen(false)} className="text-xs uppercase tracking-widest text-cyan-300">{col.name}</Link>
                <div className="mt-1 flex flex-wrap gap-x-3 gap-y-1">
                  {col.items.map((it) => (
                    <Link key={it.hash} to={`/platform/${col.id}#${it.hash}`} onClick={() => setOpen(false)} className="text-xs text-slate-400 hover:text-slate-100">{it.label}</Link>
                  ))}
                </div>
              </div>
            ))}
            <div className="h-px bg-white/5 my-2" />
            <Link to="/industries" onClick={() => setOpen(false)} className="py-2 text-sm text-slate-200 font-semibold">Industries</Link>
            {INDUSTRIES_MENU.map((row) => (
              <Link
                key={row.id}
                to={`/industries/${row.id}`}
                onClick={() => setOpen(false)}
                className="pl-3 py-1 text-xs text-slate-400 hover:text-slate-100"
              >
                {row.name}
              </Link>
            ))}
            <div className="h-px bg-white/5 my-2" />
            <Link to="/company/about" onClick={() => setOpen(false)} className="py-2 text-sm text-slate-200 font-semibold">Company</Link>
            {COMPANY_MENU.map((row) => (
              <Link key={row.id} to={row.to} onClick={() => setOpen(false)} className="pl-3 py-1 text-xs text-slate-400 hover:text-slate-100">{row.name}</Link>
            ))}
            <div className="h-px bg-white/5 my-2" />
            <Link to="/resources/blog" onClick={() => setOpen(false)} className="py-2 text-sm text-slate-200 font-semibold">Resources</Link>
            {RESOURCES_MENU.map((row) => (
              <Link key={row.id} to={row.to} onClick={() => setOpen(false)} className="pl-3 py-1 text-xs text-slate-400 hover:text-slate-100">{row.name}</Link>
            ))}
            <div className="h-px bg-white/5 my-2" />
            <Link to="/services" onClick={() => setOpen(false)} className="py-2 text-sm text-slate-200 font-semibold">Services</Link>
            <div className="pl-3 flex flex-wrap gap-x-3 gap-y-1">
              {SERVICES_MENU.map((row) => (
                <Link key={row.id} to={row.to} onClick={() => setOpen(false)} className="py-1 text-xs text-slate-400 hover:text-slate-100">{row.name}</Link>
              ))}
            </div>
            <div className="h-px bg-white/5 my-2" />
            <Link to="/partners" onClick={() => setOpen(false)} className="py-2 text-sm text-slate-200 font-semibold">Partners</Link>
            {PARTNERS_MENU.map((row) => (
              <Link key={row.id} to={row.to} onClick={() => setOpen(false)} className="pl-3 py-1 text-xs text-slate-400 hover:text-slate-100">{row.name}</Link>
            ))}
            <div className="h-px bg-white/5 my-2" />
            {nav.map((n) => (
              <Link key={n.to} to={n.to} onClick={() => setOpen(false)} className="py-2 text-sm text-slate-200">{n.label}</Link>
            ))}
            <div className="h-px bg-white/5 my-2" />
            {user ? (
              <>
                <Link to={user.role === 'partner' ? '/partner/dashboard' : '/dashboard'} onClick={() => setOpen(false)} className="py-2 text-sm text-cyan-300">{user.name}</Link>
                <Link to="/account" onClick={() => setOpen(false)} className="py-2 text-sm text-slate-300">Account</Link>
                <button onClick={() => { logout(); setOpen(false); navigate('/'); }} className="py-2 text-sm text-slate-300 text-left">Log out</button>
              </>
            ) : (
              <>
                <Link to="/login" onClick={() => setOpen(false)} className="py-2 text-sm text-slate-200">Log in</Link>
                <Link to="/contact" onClick={() => setOpen(false)} className="py-2 text-sm text-cyan-300">Book a demo</Link>
              </>
            )}
          </div>
        </div>
      )}
    </header>
  );
}
